/**
 * Mount a third-party iframe (a Stripe Element) INSIDE the widget's layout
 * while keeping it in the light DOM.
 *
 * The widget renders inside a shadow root; Stripe's iframes are not
 * reliable inside one, which is why the Card Element step portals its
 * iframe to <body> and draws it over a placeholder. That works for a 48px
 * card field. It does not work for a Payment Element that is taller than
 * the screen (Be You, 2026-10-12): the portal sits OUTSIDE the widget's
 * scroll container, so it is not clipped by it (it drew over the header)
 * and the wheel / touch over it scrolls the page behind the overlay —
 * which is locked — so the customer could not scroll back up.
 *
 * A <slot> fixes both natively. The node stays a light-DOM child of the
 * shadow HOST (where Stripe is happy), and the browser lays it out at the
 * slot — inside the scroll container, clipped by it, scrolling with it.
 * Nothing to position, observe or sync.
 *
 * Outside a shadow root (the dev harness) it just mounts in place.
 */
let seq = 0;

export interface SlotMount {
  /** Where the iframe is mounted. */
  node: HTMLElement;
  remove(): void;
}

export function createSlotMount(placeholder: HTMLElement, label: string): SlotMount {
  const root = placeholder.getRootNode();
  const node = document.createElement('div');
  node.style.width = '100%';
  if (!(root instanceof ShadowRoot)) {
    placeholder.appendChild(node);
    return { node, remove: () => node.remove() };
  }
  // Unique per mount: two widgets (or a re-mount) on one host must not
  // land in each other's slot.
  const name = `sbw-${label}-${++seq}`;
  const slot = document.createElement('slot');
  slot.name = name;
  placeholder.appendChild(slot);
  node.slot = name;
  root.host.appendChild(node);
  return {
    node,
    remove: () => {
      node.remove();
      slot.remove();
    },
  };
}
